import { useTranslation } from '@thoughtspot/i18n';
import { UserAction, userActionTypeEnum, } from '@thoughtspot/metrics';
import { BannerAlert, BannerAlertType, } from '@thoughtspot/radiant-react/widgets/alert/banner/banner-alert';
import { Vertical } from '@thoughtspot/radiant-react/widgets/layout';
import { ModalSize } from '@thoughtspot/radiant-react/widgets/modal';
import { WizardModal, } from '@thoughtspot/radiant-react/widgets/wizard-modal';
import cx from 'classnames';
import React, { useEffect, useRef, useState } from 'react';
import { useCreateGitConfig, useFetchGitBranches, useUpdateGitConfig, } from '../../../services/version-control/version-control';
import { ALLORG, ConfigModalStepsTitle, ModalActions, TranslationKeys, } from '../constant';
import BranchSelectionFormAllOrgs from './branch-selection-all-org/branch-selection-all-orgs-form';
import BranchSelectionForm from './branch-selection-specific-org/branch-selection-form';
import VersionControlConfigForm from './config-form/configuration-modal-config-form';
import styles from './configuration-modal.module.scss';
import { errorMessageMap, formatBranchList, getErrorList, getErrorMessage, sanitizeConfigForm, validateConfigForm, } from './configuration-modal.util';
export const ConfigurationModal = ({ isEditModal = false, configDetail, isModalOpen, setIsModalOpen, currentOrgId = '-1', collectEvent, }) => {
    const { t } = useTranslation();
    const isMountedRef = useRef(true);
    const [validationErrorMap, setValidationErrorMap] = useState({});
    const configFormStateRef = useRef({});
    const branchSelectionFormStateRef = useRef({ enableVersionHistory: true });
    const [branchSelectionAllOrg, setBranchSelectionAllOrg] = useState([]);
    const [remoteBranches, setRemoteBranches] = useState([]);
    const [remoteConfigBranches, setRemoteConfigBranches] = useState([]);
    const [errorMessage, setErrorMessage] = useState('');
    const [errorCode, setErrorCode] = useState(null);
    const [allOrgBranchSelection, setAllOrgBranchSelection] = useState(false);
    const [allOrgErrorList, setAllOrgErrorList] = useState([]);
    const callbackRef = useRef();
    const isAllOrg = currentOrgId === ALLORG.ID;
    useEffect(() => {
        return () => {
            isMountedRef.current = false;
        };
    }, []);
    const { data: fetchBranchesData, error: fetchBranchesError, loading: fetchBranchesLoading, fetchBranches, } = useFetchGitBranches();
    const { data: createConfigData, error: createConfigError, loading: createConfigLoading, createConfig, } = useCreateGitConfig();
    const { data: updateConfigData, error: updateConfigError, loading: updateConfigLoading, updateConfig, } = useUpdateGitConfig();
    useEffect(() => {
        if (isEditModal) {
            const { repository_url, username, commit_branch_name, configuration_branch_name, enable_version_history, } = configDetail;
            configFormStateRef.current = {
                repository: repository_url,
                username,
                token: '',
            };
            branchSelectionFormStateRef.current = {
                branch: commit_branch_name,
                enableVersionHistory: enable_version_history,
                guidBranch: configuration_branch_name,
            };
        }
    }, [isEditModal]);
    const onClickSubmitButton = async (currentStep, callback) => {
        setErrorMessage('');
        callbackRef.current = callback;
        if (currentStep === 0) {
            const errorMap = validateConfigForm(configFormStateRef.current, isEditModal, configDetail);
            setValidationErrorMap(errorMap);
            if (Object.keys(errorMap).length > 1) {
                setErrorMessage(t(TranslationKeys.VCS_ADMIN_CONFIG_FIELDS_REQUIRED));
            }
            else {
                setErrorMessage(errorMessageMap[Object.keys(errorMap)[0]]);
            }
            if (Object.keys(errorMap).length === 0) {
                configFormStateRef.current = sanitizeConfigForm(configFormStateRef.current);
                const { repository, username, token, } = configFormStateRef.current;
                fetchBranches({
                    repository_url: repository,
                    username,
                    access_token: token,
                    org_identifier: isAllOrg ? '0' : currentOrgId,
                });
            }
        }
        else {
            const { repository, username, token } = configFormStateRef.current;
            const { branch, enableVersionHistory, guidBranch, } = branchSelectionFormStateRef.current;
            const allOrgConfigs = branchSelectionAllOrg.filter(data => !!data.commit_branch_name);
            const branchNotSelectionError = isAllOrg
                ? allOrgConfigs.length === 0
                : !branch;
            const guidBranchNotSelectionError = isAllOrg
                ? !allOrgConfigs.some(data => !!data.configuration_branch_name)
                : !guidBranch;
            if (branchNotSelectionError) {
                if (isAllOrg) {
                    setErrorMessage(t(TranslationKeys.VCS_ADMIN_NO_BRANCH_ALL_ORGS_ERROR_MESSAGE));
                }
                else {
                    setErrorMessage(t(TranslationKeys.VCS_ADMIN_NO_BRANCH_ERROR_MESSAGE));
                }
            }
            else if (guidBranchNotSelectionError) {
                if (isAllOrg) {
                    setErrorMessage(t(TranslationKeys.VCS_ADMIN_GUID_NO_BRANCH_ALL_ORG_ERROR));
                }
                else {
                    setErrorMessage(t(TranslationKeys.VCS_ADMIN_GUID_NO_BRANCH_ERROR));
                }
            }
            else {
                const checkCredentialChanges = repository !== configDetail?.repository_url ||
                    username !== configDetail?.username ||
                    token !== '';
                const configParams = {
                    ...(checkCredentialChanges && {
                        repository_url: repository,
                        username,
                        access_token: token,
                    }),
                    ...(isAllOrg
                        ? {
                            org_identifier: ALLORG.TEMPID,
                            org_config: JSON.stringify(allOrgConfigs),
                        }
                        : {
                            org_identifier: currentOrgId,
                            commit_branch_name: branch ?? '',
                            enable_version_history: enableVersionHistory,
                            configuration_branch_name: guidBranch ?? '',
                        }),
                };
                if (isMountedRef.current && collectEvent) {
                    collectEvent(new UserAction(userActionTypeEnum.VERSION_CONTROL_SAVE_HISTORY), { enable_version_history: enableVersionHistory });
                }
                if (isEditModal) {
                    updateConfig(configParams);
                }
                else {
                    createConfig(configParams);
                }
            }
        }
    };
    const onClickBackButton = async (currentStep, callback) => {
        setErrorMessage('');
        setBranchSelectionAllOrg([]);
        if (currentStep === 1) {
            setAllOrgBranchSelection(false);
            callback();
        }
    };
    useEffect(() => {
        if (fetchBranchesData && !fetchBranchesLoading) {
            setRemoteBranches(formatBranchList(fetchBranchesData, true));
            setRemoteConfigBranches(formatBranchList(fetchBranchesData));
            setErrorMessage('');
            if (isAllOrg) {
                setAllOrgBranchSelection(true);
            }
            if (!branchSelectionFormStateRef.current.guidBranch &&
                "ts_config_files" in fetchBranchesData) {
                branchSelectionFormStateRef.current.guidBranch =
                    "ts_config_files";
            }
            callbackRef.current();
            if (isMountedRef.current && collectEvent) {
                collectEvent(new UserAction(userActionTypeEnum.VERSION_CONTROL_BRANCH_SUCCESS), {});
            }
        }
        if (fetchBranchesError && !fetchBranchesLoading) {
            const errorObj = getErrorMessage(fetchBranchesError);
            setErrorMessage(errorObj.message);
            setErrorCode(errorObj.errorCd);
            if (isMountedRef.current && collectEvent) {
                collectEvent(new UserAction(userActionTypeEnum.VERSION_CONTROL_BRANCH_FAILS), {});
            }
        }
    }, [fetchBranchesData, fetchBranchesLoading, fetchBranchesError]);
    useEffect(() => {
        if (createConfigData && !createConfigLoading) {
            if (isMountedRef.current && collectEvent) {
                collectEvent(new UserAction(userActionTypeEnum.VERSION_CONTROL_CONFIGURE_SUCCESS), {});
            }
            setIsModalOpen(ModalActions.CONFIRM);
        }
        if (createConfigError && !createConfigLoading) {
            if (!isAllOrg) {
                const errorObj = getErrorMessage(createConfigError);
                setErrorMessage(errorObj.message);
                setErrorCode(errorObj.errorCd);
                if (isMountedRef.current && collectEvent) {
                    collectEvent(new UserAction(userActionTypeEnum.VERSION_CONTROL_CONFIGURE_FAILS), { ...errorObj });
                }
            }
            else {
                const errorList = getErrorList(createConfigError);
                setAllOrgErrorList(errorList);
            }
        }
    }, [createConfigData, createConfigLoading, createConfigError]);
    useEffect(() => {
        if (updateConfigData && !updateConfigLoading) {
            setIsModalOpen(ModalActions.CONFIRM, isEditModal);
        }
        if (updateConfigError && !updateConfigLoading) {
            if (!isAllOrg) {
                const errorObj = getErrorMessage(updateConfigError);
                setErrorMessage(errorObj.message);
                setErrorCode(errorObj.errorCd);
            }
            else {
                const errorList = getErrorList(updateConfigError);
                setAllOrgErrorList(errorList);
            }
        }
    }, [updateConfigData, updateConfigLoading, updateConfigError]);
    const handleBranchSelectionChangeAllOrgs = (orgId, orgConfigObj) => {
        const updatedArray = branchSelectionAllOrg.map(config => config.org_identifier !== orgId ? config : orgConfigObj);
        if (!updatedArray.some(config => config.org_identifier === orgId)) {
            updatedArray.push(orgConfigObj);
        }
        setBranchSelectionAllOrg(updatedArray);
    };
    const dismissModal = () => {
        setIsModalOpen(ModalActions.CANCEL);
    };
    const retryLink = {
        label: t(TranslationKeys.VCS_RETRY),
        link: null,
        onClick: () => {
            onClickSubmitButton(0, () => { });
        },
    };
    const commonLink = {
        label: t(TranslationKeys.VCS_CONTACT_SUPPORT),
        openLinkInNewTab: true,
        link: 'https://docs.thoughtspot.com/cloud/latest/support-contact',
    };
    const errorCodeToLink = {
        '15002': retryLink,
        '-1': commonLink,
    };
    const renderAlertmessage = () => {
        const warningText = t(TranslationKeys.VCS_ADMIN_EDIT_CONFIGURATION_WARNING_TEXT);
        return (React.createElement(BannerAlert, { className: cx(styles.errorBannerAlert, {
                [styles.errorBannerAlert__allOrgBranchSelection]: allOrgBranchSelection,
            }), isVisible: isEditModal || !!errorMessage, message: isEditModal && !errorMessage ? warningText : errorMessage, showCloseButton: false, type: isEditModal && !errorMessage
                ? BannerAlertType.WARNING
                : BannerAlertType.ERROR, link: errorCodeToLink[errorCode] || null }));
    };
    const modalSteps = [
        {
            stepTitle: ConfigModalStepsTitle.STEP1TITLE,
            stepContent: (React.createElement(Vertical, { grow: true },
                renderAlertmessage(),
                React.createElement(VersionControlConfigForm, { formRef: configFormStateRef, validationErrorMap: validationErrorMap, isEditModal: isEditModal }))),
            stepForwardButton: {
                text: t('continue'),
                onNext: onClickSubmitButton,
            },
        },
        {
            stepTitle: ConfigModalStepsTitle.STEP2TITLE,
            stepContent: (React.createElement(Vertical, { grow: true },
                renderAlertmessage(),
                isAllOrg ? (React.createElement(BranchSelectionFormAllOrgs, { onBranchStateChange: handleBranchSelectionChangeAllOrgs, branches: remoteBranches, configBranches: remoteConfigBranches, branchSelectionsState: branchSelectionAllOrg, errorList: allOrgErrorList })) : (React.createElement(BranchSelectionForm, { formRef: branchSelectionFormStateRef, branches: remoteBranches, configBranches: remoteConfigBranches })))),
            stepForwardButton: {
                text: isEditModal ? t('UPDATE') : t('status.Enable'),
                onNext: onClickSubmitButton,
            },
            stepBackButton: {
                text: t('BACK'),
                onBack: onClickBackButton,
            },
        },
    ];
    return (React.createElement(WizardModal, { title: t(TranslationKeys.VCS_ADMIN_CONFIG_MODAL_TITLE), isOpen: isModalOpen, denyButton: {
            text: t('CANCEL'),
            onDeny: dismissModal,
        }, wizardModalStep: modalSteps, className: styles.configurationModal, isProgressBarEnabled: true, onDismiss: dismissModal, doNotDismissOnEsc: true, doNotDismissOnOutsideClick: true, noPadding: isAllOrg && allOrgBranchSelection, autoScrollContent: true, size: ModalSize.MEDIUM, isContentLoading: fetchBranchesLoading ||
            createConfigLoading ||
            updateConfigLoading }));
};
//# sourceMappingURL=configuration-modal.js.map